import type { Dispatch, ReactNode, RefObject, SetStateAction } from 'react';
import { cloneElement, isValidElement, useLayoutEffect } from 'react';
import { Drawer } from 'vaul';
import { MOBILE_ANALYSIS_SHEET_STACKING, MOBILE_SHEET_BOTTOM_OFFSET } from './analysisMobileSheetConstants';

export type AnalysisMobileBottomSheetProps = {
    measureEl: HTMLDivElement | null;
    setMeasureEl: Dispatch<SetStateAction<HTMLDivElement | null>>;
    mobileMinHeightPx: string;
    mobilePeekHeightPx: string;
    mobileExpandedHeightPx: string;
    snapPoint: number | string | null;
    setSnapPoint: Dispatch<SetStateAction<number | string | null>>;
    peekContentRef: RefObject<HTMLDivElement | null>;
    isExpanded: boolean;
    searchAreaTitle: string;
    analysisToolbarMiddle: ReactNode;
    analysisToolbarDock?: ReactNode;
    analysisPeekSummary: ReactNode;
    /** null のときスクロール枠なし */
    analysisMobileExpandedScrollContent: ReactNode | null;
    sheetOpen: boolean;
    setSheetOpen: Dispatch<SetStateAction<boolean>>;
    sheetReopenAriaLabel: string;
};

/**
 * 分析ページ・モバイル: Vaul ボトムシート（3 スナップ + ツールバー差し込み）
 */
export function AnalysisMobileBottomSheet({
    measureEl,
    setMeasureEl,
    mobileMinHeightPx,
    mobilePeekHeightPx,
    mobileExpandedHeightPx,
    snapPoint,
    setSnapPoint,
    peekContentRef,
    isExpanded,
    searchAreaTitle,
    analysisToolbarMiddle,
    analysisToolbarDock,
    analysisPeekSummary,
    analysisMobileExpandedScrollContent,
    sheetOpen,
    setSheetOpen,
    sheetReopenAriaLabel,
}: AnalysisMobileBottomSheetProps) {
    /**
     * modal={false} のため Vaul の本文スクロールロックが効かない。展開スナップ時だけ背後ページのスクロールを止め、
     * シート内の overflow 領域がスクロール対象になるようにする（ピーク時は背後操作のためロックしない）。
     */
    useLayoutEffect(() => {
        if (!sheetOpen || !isExpanded) return;
        const scrollY = window.scrollY;
        const body = document.body;
        const html = document.documentElement;
        const prevBodyOverflow = body.style.overflow;
        const prevBodyPosition = body.style.position;
        const prevBodyTop = body.style.top;
        const prevBodyWidth = body.style.width;
        const prevHtmlOverflow = html.style.overflow;

        body.style.overflow = 'hidden';
        html.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.width = '100%';

        return () => {
            body.style.overflow = prevBodyOverflow;
            body.style.position = prevBodyPosition;
            body.style.top = prevBodyTop;
            body.style.width = prevBodyWidth;
            html.style.overflow = prevHtmlOverflow;
            window.scrollTo(0, scrollY);
        };
    }, [sheetOpen, isExpanded]);

    return (
        <>
            <div
                ref={setMeasureEl}
                className={`pointer-events-none fixed top-0 right-0 left-0 ${MOBILE_ANALYSIS_SHEET_STACKING} overflow-visible`}
                style={{ bottom: MOBILE_SHEET_BOTTOM_OFFSET }}
            />

            {measureEl && (
                <Drawer.Root
                    open={sheetOpen}
                    onOpenChange={setSheetOpen}
                    dismissible
                    modal={false}
                    container={measureEl}
                    snapPoints={[mobileMinHeightPx, mobilePeekHeightPx, mobileExpandedHeightPx]}
                    fadeFromIndex={2}
                    snapToSequentialPoint
                    activeSnapPoint={snapPoint}
                    setActiveSnapPoint={setSnapPoint}
                    shouldScaleBackground={false}
                >
                    <Drawer.Portal container={measureEl}>
                        {/* 
                            modal={false} の場合、vaul の Overlay は正常に動作しないことが多いため、
                            Portal 内に自前の div で背景レイヤーを配置する。
                        */}
                        <div
                            className="fixed inset-0 bg-black/40 transition-opacity duration-300"
                            style={{
                                pointerEvents: isExpanded ? 'auto' : 'none',
                                opacity: isExpanded ? 1 : 0,
                                zIndex: -1,
                                // measureEl の bottom 制限を無視して画面全体を覆う
                                height: '100vh',
                                width: '100vw',
                                top: 0,
                                left: 0,
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSnapPoint(mobilePeekHeightPx);
                            }}
                        />
                        <Drawer.Content
                            id="analysis-mobile-bottom-sheet"
                            className="pointer-events-auto absolute inset-x-0 top-0 flex flex-col bg-white rounded-t-xl shadow-[0_-4px_16px_rgba(0,0,0,0.12)] border border-gray-200 outline-none"
                            style={{ height: mobileExpandedHeightPx }}
                            aria-describedby={undefined}
                        >
                            <Drawer.Title className="sr-only">{searchAreaTitle}</Drawer.Title>
                            
                            {/* ハンドル部分 (Min state) */}
                            <div className="flex w-full shrink-0 justify-center pt-2.5 pb-2 h-[32px]">
                                <Drawer.Handle className="!h-1 !w-24 shrink-0 !rounded-full !bg-gray-300 !opacity-100" />
                            </div>

                            {/* スクロール領域 (シート内全体) */}
                            <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]">
                                {/* ツールバー・ドック部分 (Peek state) */}
                                <div ref={peekContentRef} className="shrink-0 flex flex-col border-b border-gray-100 px-1.5 sm:px-3">
                                    <div
                                        className={
                                            isExpanded
                                                ? 'flex flex-col gap-5 px-1 pb-2 sm:px-2'
                                                : 'flex flex-col gap-4 px-1 pb-3 sm:px-2'
                                        }
                                    >
                                        {analysisToolbarMiddle != null ? (
                                            <div className="w-full px-1">
                                                {isValidElement(analysisToolbarMiddle)
                                                    ? cloneElement(analysisToolbarMiddle, {
                                                          omitTutorialDateNavId: true,
                                                          hideDateNavigation: true,
                                                          compactBucketTabs: !isExpanded,
                                                      } as any)
                                                    : analysisToolbarMiddle}
                                            </div>
                                        ) : null}
                                        {analysisToolbarDock != null ? (
                                            <div data-analysis-dock-interactive className="w-full">
                                                {isValidElement(analysisToolbarDock)
                                                    ? cloneElement(analysisToolbarDock, {
                                                          dockComfortableHeight: isExpanded,
                                                      } as any)
                                                    : analysisToolbarDock}
                                            </div>
                                        ) : analysisPeekSummary ? (
                                            <div className="py-1 text-center text-xs text-gray-600">
                                                {analysisPeekSummary}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>

                                {/* 展開時のスクロールコンテンツ (Expanded state) */}
                                {analysisMobileExpandedScrollContent != null ? (
                                    <div className="px-4 pb-3 pt-1">
                                        {analysisMobileExpandedScrollContent}
                                    </div>
                                ) : null}
                            </div>
                        </Drawer.Content>
                    </Drawer.Portal>
                </Drawer.Root>
            )}

            {measureEl && !sheetOpen ? (
                <div
                    className={`pointer-events-auto fixed right-3 left-3 flex justify-center ${MOBILE_ANALYSIS_SHEET_STACKING}`}
                    style={{ bottom: `calc(${MOBILE_SHEET_BOTTOM_OFFSET} + 10px)` }}
                >
                    <button
                        type="button"
                        id="analysis-mobile-sheet-reopen"
                        className="flex w-full max-w-lg flex-col items-center rounded-t-xl border border-gray-200 bg-white px-4 pt-2.5 pb-2 shadow-[0_-4px_16px_rgba(0,0,0,0.12)]"
                        aria-label={sheetReopenAriaLabel}
                        onClick={() => {
                            setSheetOpen(true);
                            setSnapPoint(mobilePeekHeightPx);
                        }}
                    >
                        <span className="!h-1 !w-24 shrink-0 rounded-full bg-gray-300" aria-hidden />
                    </button>
                </div>
            ) : null}
        </>
    );
}
